import React, { useState, useEffect, useMemo } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Search, UserSearch, UserPlus, QrCode } from 'lucide-react'
import { Alert } from '@repo/ui'
import { suggestEmailCorrection, validateEmailAddress } from '@repo/types'
import { DrawerPopup } from 'components/UI/DrawerPopup'
import { UserPreview } from 'components/UI'
import { QrScanner } from 'components/UI/QrScanner'
import { SectionLabel } from 'components/UI/SectionLabel'
import { PICKER_SHEET } from 'components/UI/pickerListStyle'
import { menuRowToneSx } from 'components/UI/menuRowTone'
import { fieldSurfaceSx, FIELD_HEIGHT } from 'components/UI/fieldMetrics'
import { Input } from 'components/UI/Input'
import { createApiCall } from 'services'
import { getCustomers } from 'store/customers/actions'
import { useDispatch, useSelector } from 'store/hooks'
import { getName } from 'utils/parsers'
import { extractProfileCode } from 'utils/profileCode'
import type { Customer } from 'types'

/**
 * The height of the RESULTS REGION, and the whole of what used to be a fixed sheet.
 *
 * VERA-304 pinned the entire drawer to `75vh` because it resized as results came and
 * went ("really jumpy and odd"). That fixed the jumpiness and bought a second problem:
 * the sheet stayed 75vh when there was nothing in it, so an idle picker showing two
 * Recent rows reserved roughly 350px of dead space below them.
 *
 * Both are addressed by moving the constraint OFF the sheet and ONTO the region that
 * actually varies, with a min that applies only in the regime that needed one:
 *
 *  - SEARCHING (`query` non-empty) — `minHeight` AND `maxHeight` are both this value,
 *    so the region is exactly fixed. Results appearing, narrowing and disappearing
 *    keystroke-to-keystroke cannot move the sheet AT ALL. This is byte-for-byte the
 *    behaviour VERA-304 asked for, kept for the case it was reported against, and it
 *    is deliberately not left to the height animation to smooth: at a 300ms debounce
 *    against a 260ms animation the sheet would be resizing almost continuously while
 *    someone types, which is the original complaint with a fade on it.
 *  - IDLE (no query — Recent, or the empty state) — only `maxHeight` applies, so the
 *    sheet is the height of its content. This is the state the dead space was in.
 *
 * Crossing between the two regimes is ONE height change (first keystroke, and
 * clearing), which is exactly the kind of change `DrawerPopup`'s animated height
 * exists to interpolate.
 *
 * 52vh is not arbitrary: sheet chrome here measures ~190px (grabber, header, the
 * field row, the Stack gap and the content padding), so 52vh + chrome lands within a
 * few px of the old 75vh sheet at a 375x812 phone. A search therefore occupies the
 * same space it always did.
 */
const RESULTS_REGION_HEIGHT = '52vh'

// VERA-304: hide the scrollbar cross-browser while keeping the results list
// scrollable.
const hiddenScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const

/**
 * VERA-304: result rows fade+rise in instead of snapping.
 *
 * Deliberately an ENTER-only animation on the rows, not a height transition on the
 * list. The drawer is a fixed 75vh precisely because it used to resize as results
 * came and went ("really jumpy and odd"), so anything that animates LAYOUT here
 * would walk straight back into that. This only animates each row's own opacity and
 * a 4px translate — no reflow, so the sheet cannot move.
 *
 * The stagger is capped: past ~8 rows the last row would start late enough to feel
 * like lag rather than polish, and the list can return 100+.
 */
const ROW_ENTER_MS = 180
const ROW_STAGGER_MS = 22
const ROW_STAGGER_CAP = 8

const rowEnterSx = (index: number) => ({
  animation: `veraPickerRowIn ${ROW_ENTER_MS}ms ease-out both`,
  animationDelay: `${Math.min(index, ROW_STAGGER_CAP) * ROW_STAGGER_MS}ms`,
  '@keyframes veraPickerRowIn': {
    from: { opacity: 0, transform: 'translateY(4px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  // Motion is decoration here — the rows are equally usable without it.
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
})

/**
 * VRG-358 — the narrow projection both new endpoints return. No `email`, by
 * design: see `CustomerLookupItemDto` on the API side and the VRG-357 note in
 * this file about why echoing an address back is an enumeration oracle.
 */
type LookupPerson = {
  id: string
  first_name?: string | null
  last_name?: string | null
  username?: string | null
  avatar?: string | null
}

/** A lookup result is only a hit if it actually carries an id. See `resolveScannedCode`. */
const isLookupPerson = (value: any): value is LookupPerson =>
  !!value && typeof value.id === 'string' && value.id.length > 0

/**
 * VRG-358: build the app's `Customer` shape from the narrow lookup projection.
 *
 * `email` is deliberately EMPTY and must stay that way — the endpoints do not
 * return one and inventing a placeholder would be worse than the gap, because it
 * would look like a working address to every downstream reader.
 *
 * TODO(VRG-358): the send side cannot yet act on a recipient identified this way.
 * `GiftCertificate.onSubmit` guards on `if (!selectedUser?.email) return`, and the
 * transfer init API takes only `toCustomerEmail` (`@IsEmail()`), so a recipient
 * picked by scan or from Recent selects correctly in this sheet and then the Gift
 * button silently does nothing. Note this is ALREADY true of the @handle search
 * path above — VRG-357 removed `email` from the lookup DTO — so the fix is one
 * change for all three paths: have the init endpoint accept the owner `id` this
 * projection exists to carry ("Owner id, used to address the transfer") and have
 * `onSubmit` guard on `!selectedUser` instead.
 */
const toCustomer = (person: LookupPerson): Customer => ({
  id: person.id,
  first_name: person.first_name ?? '',
  last_name: person.last_name ?? '',
  username: person.username ?? '',
  avatar: person.avatar ?? '',
  email: '',
  phone: '',
  birthday: null,
  active: true,
  created_at: '',
  updated_at: '',
})

const SCAN_NOT_FOUND_TITLE = "That code didn't match anyone"
const SCAN_NOT_FOUND_BODY =
  'Ask them to open Vera, then Profile, then My code for a fresh one.'
const SCAN_FAILED_TITLE = "Couldn't check that code"
const SCAN_FAILED_BODY = 'Check your connection and try again.'

/**
 * One selectable person row. Extracted on its second use (working-conventions §36)
 * when Recent arrived — results and Recent are the same row and must not drift into
 * two treatments of one thing. Local to this file rather than in `components/UI`
 * because it is the picker's row, not a general one; promote it if a third surface
 * wants it.
 */
const RecipientRow = ({
  customer,
  index,
  showDivider,
  onSelect,
}: {
  customer: Customer
  index: number
  showDivider: boolean
  onSelect: (customer: Customer) => void
}) => (
  <Box
    onClick={() => onSelect(customer)}
    sx={theme => ({
      // VERA-304: reuse the same rounded-hover row treatment as
      // ActionMenu's bottom-sheet rows (working-conventions §28)
      // instead of a square full-bleed hover.
      px: PICKER_SHEET.itemPX,
      py: PICKER_SHEET.itemPY,
      borderRadius: `${PICKER_SHEET.radius}px`,
      ...rowEnterSx(index),
      cursor: 'pointer',
      borderBottom: showDivider ? `1px solid ${theme.palette.divider}` : 'none',
      // Was a grey[50] wash with no text change — half the shared treatment. The
      // row now also mutes at rest and picks up the brand accent on hover/focus.
      // No `selected`: picking a recipient closes the sheet, so no row is ever
      // left in a chosen state. `UserPreview`'s name line carries no colour of its
      // own, so it inherits this automatically; its @handle sublabel sets
      // `text.secondary` explicitly and so stays muted at every state.
      ...menuRowToneSx(theme),
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    })}
  >
    {/* VERA-304: identity comes from the shared resolver. This row
        used to fall back to the EMAIL as its primary line, which
        echoes back the address the searcher typed and so confirms
        "this account exists" for any address they try. It now falls
        back to the @handle; the email appears only for an
        invite-by-email person, who has no account to confirm. */}
    <UserPreview user={customer} avatarUrl={customer.avatar} />
  </Box>
)

export type RecipientSearchPickerProps = {
  open: boolean
  onClose: () => void
  onSelectRecipient: (customer: Customer) => void
  /**
   * The sheet's heading. Defaults to the gift flow's wording, which is what every
   * caller wanted until the sell sheet reused this picker and got "Gift to" over a
   * sale — a heading that names the wrong transaction entirely.
   *
   * A prop rather than a rename because the two flows genuinely say different things
   * here, and the gift wording is already the one a reader meets most. VERA-632 settles
   * this differently, by titling it after the FIELD ("Recipient") for both; when that
   * lands, take its version and drop this prop.
   */
  title?: string
}

export const RecipientSearchPicker = ({
  open,
  onClose,
  onSelectRecipient,
  title = 'Gift to',
}: RecipientSearchPickerProps) => {
  const dispatch = useDispatch()
  const { customers } = useSelector(({ customers }) => ({
    customers: customers.customers,
  }))

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)

  // VRG-358 — scan-a-code state.
  const [scanOpen, setScanOpen] = useState(false)
  const [resolvingCode, setResolvingCode] = useState(false)
  const [scanError, setScanError] = useState<{ title: string; body: string } | null>(null)

  // VRG-358 — people this caller has already sent to. Owner-scoped server-side, so
  // it discloses nothing new (see the endpoint's own note).
  const [recent, setRecent] = useState<Customer[]>([])

  /**
   * Has the Recent request SETTLED? Distinct from `recent.length === 0`, which is also
   * true for the moment before the response lands.
   *
   * Load-bearing for the empty state below. Gating that block on `recent.length === 0`
   * alone would render "there is nothing here, type a handle" on every open, then
   * replace it with a list a beat later — telling the user the surface is empty
   * immediately before showing them it is not.
   */
  const [recentLoaded, setRecentLoaded] = useState(false)

  useEffect(() => {
    if (open) {
      setQuery('')
      setScanError(null)
      setRecentLoaded(false)
    }
  }, [open])

  /**
   * VRG-358: load Recent when the sheet opens.
   *
   * `createApiCall` RESOLVES on an HTTP error (its `errorHandler` returns
   * `error.response` unless `throwError` is set), so a failure arrives here as a
   * normal response with no `data.data` — never as a rejection. Hence the explicit
   * shape check rather than a try/catch as the primary guard: a 401 or a 500 must
   * leave `recent` empty, not crash on `.map` of undefined.
   */
  useEffect(() => {
    if (!open) return
    let cancelled = false

    const load = async () => {
      try {
        const response = await createApiCall({
          method: 'GET',
          url: '/customers/public/recent-recipients',
        })
        if (cancelled) return
        const rows = response?.data?.data
        setRecent(Array.isArray(rows) ? rows.filter(isLookupPerson).map(toCustomer) : [])
      } catch {
        if (!cancelled) setRecent([])
      } finally {
        // In `finally`, so a failed load still releases the empty state. A 401 or a 500
        // leaves `recent` empty, and "no recents" is the correct thing to show for it —
        // what must not happen is the picker sitting with no guidance forever because
        // the flag never flipped.
        if (!cancelled) setRecentLoaded(true)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [open])

  const handleSearch = async (searchQuery: string) => {
    if (!searchQuery.trim()) return
    
    setLoading(true)
    try {
      dispatch(getCustomers({ query: searchQuery.trim(), limit: 10 }))
    } catch (error) {
      console.error('Error searching customers:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (query.trim()) {
      const timeoutId = setTimeout(() => {
        handleSearch(query)
      }, 300)
      
      return () => clearTimeout(timeoutId)
    }
  }, [query])

  const handleSelectCustomer = (customer: Customer) => {
    onSelectRecipient(customer)
    onClose()
  }

  const handleClear = () => {
    setQuery('')
  }

  /**
   * VRG-358: turn a scanned string into a selected recipient.
   *
   * The 404 handling is the load-bearing part. `createApiCall` does NOT reject on
   * an HTTP error — its `errorHandler` resolves with `error.response` unless
   * `throwError: true` is passed — so a `try/catch` around this call would never
   * see a 404 at all: the miss would arrive as an apparent success carrying
   * `data: {message: 'Profile not found'}`, and selecting on truthiness would hand
   * the gift sheet a recipient with no id. So the hit is decided by VALIDATING THE
   * PAYLOAD (`isLookupPerson`), and the two failure messages are distinguished by
   * status: a 404 is a real "no such code", anything else is a request that did not
   * complete and is worth retrying. The catch below is only for a thrown
   * client-side error (a malformed URL, say), not for HTTP status.
   */
  const resolveScannedCode = async (raw: string) => {
    setScanOpen(false)
    setScanError(null)

    const code = extractProfileCode(raw)
    if (!code) {
      setScanError({ title: SCAN_NOT_FOUND_TITLE, body: SCAN_NOT_FOUND_BODY })
      return
    }

    setResolvingCode(true)
    try {
      const response = await createApiCall({
        method: 'GET',
        url: `/auth/profile-code/${encodeURIComponent(code)}`,
      })

      if (isLookupPerson(response?.data)) {
        handleSelectCustomer(toCustomer(response.data))
        return
      }

      setScanError(
        response?.status === 404
          ? { title: SCAN_NOT_FOUND_TITLE, body: SCAN_NOT_FOUND_BODY }
          : { title: SCAN_FAILED_TITLE, body: SCAN_FAILED_BODY },
      )
    } catch {
      setScanError({ title: SCAN_FAILED_TITLE, body: SCAN_FAILED_BODY })
    } finally {
      setResolvingCode(false)
    }
  }

  /**
   * VRG-471 — was a local syntax-only regex, shared by copy with the platform's
   * transfer modal. Syntax is exactly what let `spencer@usevergence.cin` through:
   * it is a perfectly well-formed address whose ending does not exist, so the
   * gift was created, an owner provisioned, and two emails silently discarded.
   *
   * `recipientCheck` now reads the shared `@repo/types` validator, the same one
   * the API enforces via `@IsDeliverableEmailDomain()`. Note the two flags are
   * NOT the same question: `looksLikeEmail` decides whether the typed text is an
   * address at all (so the card appears instead of nothing), `isDeliverable`
   * decides whether it can be sent to.
   */
  const recipientCheck = useMemo(() => {
    const trimmed = query.trim()
    const result = validateEmailAddress(trimmed)

    return {
      looksLikeEmail: result.ok || result.code === 'unknown_tld',
      isDeliverable: result.ok,
      reason: result.message,
      // Advisory only — never gates the Add button.
      suggestion: suggestEmailCorrection(trimmed),
    }
  }, [query])

  const isEmail = (text: string) => {
    const result = validateEmailAddress(text)
    return result.ok || result.code === 'unknown_tld'
  }

  const handleInviteUser = (email: string = query.trim()) => {
    if (validateEmailAddress(email).ok) {
      const mockCustomer: Customer = {
        id: `invite-${Date.now()}`,
        // The PARAMETER, not `query` — so accepting a "Did you mean …?" actually
        // gifts to the corrected address rather than the typo it replaced.
        email,
        first_name: '',
        last_name: '',
        username: '',
        avatar: '',
        phone: '',
        birthday: null,
        active: true,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      onSelectRecipient(mockCustomer)
      onClose()
    }
  }

  /*
   * No `paperSx` height override any more, and no `contentHeight` / `contentSx`.
   *
   * `DrawerPopup`'s own default for a bottom sheet is `height: auto` with a `90vh`
   * safety cap, which is what every other sheet in the app uses — the picker no longer
   * needs to be the exception. Its height is now chrome + whatever
   * `RESULTS_REGION_HEIGHT` resolves to, which tops out well under that cap, so the
   * 90vh never binds.
   *
   * `contentHeight="100%"` and the `flex: 1, minHeight: 0` `contentSx` existed only to
   * build a flex chain that could hand a DEFINITE height to the inner scroll region —
   * a flex item cannot resolve `flex: 1` against a parent that is itself `auto`. With
   * the constraint expressed as a plain `max-height` on that region directly, nothing
   * needs a definite height anywhere: every box is content-sized and the cap clamps.
   */
  return (
    <DrawerPopup
      title={title}
      rightActionLabel="Done"
      rightActionOnClick={onClose}
      child={
        <Stack spacing={3}>
          {/* VERA-304: the shared field surface, not a third hand-rolled copy. This box
              declared its own `borderRadius: '12px'` + `grey[300]` border, which is the
              exact drift `fieldSurfaceSx` documents itself as existing to stop — and the
              hardcoded 12px bypassed the radius clamp (which resolves to 6). The trigger
              in GiftCertificate that OPENS this drawer now uses the same helper, so the
              control you tap and the control you land on finally match. */}
          {/* VRG-358: the scan button sits ON the field row, as a peer of the search
              box rather than below it, because they are two ways to answer the same
              question ("who is this going to?"). `fieldMetrics` documents exactly this
              pairing — "the search trigger, the date trigger, the NFC button beside
              them" — so it takes the same surface and the same FIELD_HEIGHT and the
              row reads as one control group. */}
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          {/* The app's BASE field with a leading magnifier — the same construction
              `SelectDrawer` and `ToolbarSearchField` already use — not a hand-rolled
              `<Box sx={surface}><Icon/><input/></Box>`.

              Two defects go away by construction rather than by being fixed. The
              surface set `color: 'text.secondary'` for its magnifier and a bare
              `<input>` INHERITS colour, so text you had TYPED rendered in the
              placeholder's grey; `Input` sets `text.primary` on the input slot. And
              the reported oversized placeholder was a FONT FAMILY, not a size: this
              app's baseline is MUI's with `font: inherit` deliberately removed, so a
              bare `<input>` keeps the UA form-control font (measured: Arial, 5% wider
              than the tenant's face at the same 16px) while MUI's own `InputBase`
              puts `font: inherit` on its input slot. Going through `Input` is the
              fix; nothing here has to declare the font at all. */}
          <Input
            value={query}
            onChange={next => {
              setQuery(next)
              // A stale "that code didn't match" banner sitting above fresh typing
              // reads as a verdict on what is being typed now.
              if (scanError) setScanError(null)
            }}
            placeholder="@username or email"
            startAdornment={<Search size={16} />}
            inputProps={{
              'aria-label': 'Search by @username or email',
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
            }}
          />

          <Box
            role="button"
            aria-label="Scan their code"
            title="Scan their code"
            onClick={() => setScanOpen(true)}
            sx={theme => ({
              ...fieldSurfaceSx(theme),
              height: FIELD_HEIGHT,
              width: FIELD_HEIGHT,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'text.primary',
              '&:hover': { backgroundColor: theme.palette.grey[100] },
            })}
          >
            <QrCode size={20} strokeWidth={1.5} />
          </Box>
          </Stack>

          {/* Everything below scrolls WITHIN this region rather than growing the sheet
              past its cap. `minHeight` is applied ONLY while a query is active, which
              is what pins the sheet completely still during a search while letting it
              size to its content when idle — see `RESULTS_REGION_HEIGHT`. */}
          <Box
            sx={{
              overflowY: 'auto',
              maxHeight: RESULTS_REGION_HEIGHT,
              ...(query ? { minHeight: RESULTS_REGION_HEIGHT } : null),
              ...hiddenScrollbarSx,
            }}
          >
          {/* VRG-358: an Alert, not bare red text — this epic already corrected one
              hand-rolled red paragraph to the shared component. It is dismissible
              because it reports a completed attempt, not standing guidance. */}
          {scanError && (
            <Box sx={{ mb: 2 }}>
              <Alert
                severity="error"
                title={scanError.title}
                description={scanError.body}
                onClose={() => setScanError(null)}
              />
            </Box>
          )}

          {resolvingCode && (
            <Typography
              variant="body2"
              textAlign="center"
              sx={theme => ({ color: theme.palette.text.secondary, py: 2, lineHeight: 1.5 })}
            >
              Looking up that code…
            </Typography>
          )}

          {/* VRG-358: Recent sits ABOVE the explanatory empty state, not below it.
              The whole point is to spare the user the typing, so it has to be the
              thing they land on. When there is no history this renders nothing at
              all — an empty "Recent" heading inside an empty state would be an
              empty state within an empty state. */}
          {!query && recent.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <SectionLabel first>Recent</SectionLabel>
              {recent.map((customer, index) => (
                <RecipientRow
                  key={customer.id}
                  customer={customer}
                  index={index}
                  showDivider={index !== recent.length - 1}
                  onSelect={handleSelectCustomer}
                />
              ))}
            </Box>
          )}

          {/*
            An EMPTY state, so it renders only when the surface is genuinely empty.

            This block used to be gated on `!query` alone, so it rendered UNDERNEATH the
            Recent list: the picker showed two people and, below them, a centred "enter
            someone's @username to find them" — telling the user there is nothing here
            while showing them something. An empty state that appears next to content is
            not an empty state, it is a caption that contradicts the screen.

            Nothing replaces it when Recent has rows, deliberately. The recents ARE the
            content, and the one thing the copy carried that the list does not — that an
            email address can be sent to directly — is already on the field itself as
            its placeholder, which is where a hint about what to type belongs.

            `recentLoaded` is the other half: without it this flashes on every open
            during the request, immediately before the list it claims does not exist.
          */}
          {!query && recentLoaded && recent.length === 0 && (
            /*
             * VERA-304 — an empty state that NAMES THE ABSENT THING, bound to the field.
             *
             * Two defects, one block. What shipped was a 60px glyph in a circle floating
             * in `py: 4` (32px) of its own padding, and copy that only explained the
             * INPUT. That is a caption, not an empty state: it never said what would
             * normally be here, so a first-time user could not tell whether the surface
             * was empty or simply had nothing to offer.
             *
             * It IS a real empty state — the picker loads `/customers/public/recent-recipients`
             * above, so this area genuinely populates for anyone who has gifted before —
             * so the fix is to say so ("No recent recipients"), not to remove it.
             *
             * The second defect was PROXIMITY. Measured at 375x812: the copy sat 132px
             * below the field it describes and 56px above the sheet's bottom edge, so the
             * helper read as belonging to the bottom of the sheet rather than to the
             * control above it. The 60px circle and its 32px of padding were the whole of
             * that 132. The glyph is now a 16px INLINE icon on the heading line, which
             * keeps the empty-state signal and returns the copy to the field: 24px above
             * (the content Stack's own spacing) against 40px below (`pb: 2` + the drawer's
             * shared 24px inset). Gap above is now smaller than gap below, which is what
             * binds it upward.
             */
            <Box sx={{ pt: 0, pb: 2 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                {/* Was 24px inside a 60px circle, hard-coded to `#000` — the one glyph on
                    this sheet that ignored the palette. At 16px on the heading line it is
                    an adornment rather than an illustration, and it takes the heading's
                    own colour. */}
                <UserSearch size={16} style={{ flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  No recent recipients
                </Typography>
              </Stack>
              {/* VRG-357: this used to promise a search over "name, phone number,
                  email, or user name". That promise was backed by
                  `email ILIKE '%q%'` across every user in the system, which is why
                  it could be kept — and why it was an enumeration oracle.

                  Two ways to address someone now, and the copy says which is which
                  so a miss does not read as the feature being broken:
                    - their @username, which they chose and share deliberately, or
                    - their email, which sends WITHOUT us confirming whether an
                      account exists (an unregistered recipient is invited to claim).

                  VERA-304: now preceded by a sentence saying what WILL appear here, so
                  the block explains the absence as well as the input. Left-aligned with
                  the heading and the field, not centred — centred text reads as an
                  island the field is not part of, which is how it drifted 132px away in
                  the first place. */}
              <Typography variant="body2" sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })}>
                People you gift to will appear here. Until then, search a @username, or
                type an email address to send it straight there. They don&apos;t need an
                account yet.
              </Typography>
            </Box>
          )}

          {/* VRG-357: an EMAIL query never renders lookup results, even if the API
              returned some. This is the no-oracle guarantee, enforced here rather than
              left to depend on the server's matching rules: if an existing address
              showed a person and an unknown one showed the invite card, the difference
              between those two screens IS the confirmation that an account exists.
              Typing an address now produces exactly one outcome — "send it there" —
              whoever they are. */}
          {customers?.data && query && !isEmail(query) && (
            // `key={query}` is what makes the enter animation actually fire on every
            // new search. Rows are keyed by `customer.id`, so React RE-USES the DOM
            // node for anyone present in both the old and new results and the CSS
            // animation never restarts — the common case, since narrowing a query
            // keeps most matches. Re-keying the container on the query remounts the
            // list, so each result set animates in as a set.
            <Box key={query}>
              {customers.data.map((customer, index) => (
                <RecipientRow
                  key={customer.id}
                  customer={customer}
                  index={index}
                  showDivider={index !== customers.data.length - 1}
                  onSelect={handleSelectCustomer}
                />
              ))}
            </Box>
          )}

          {customers?.data && customers.data.length === 0 && query && !isEmail(query) && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              {/* A handle miss is a real "no such handle" — and that is fine to say.
                  A @username is a public identifier its owner chose to publish, so
                  confirming one exists reveals nothing they did not intend. That is
                  exactly why the lookup is handle-only: it moves the confirmable
                  surface off the address people use to log in. */}
              <Typography variant="body2" sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })} textAlign="center">
                No one with that username. Check the spelling, or use their email
                address instead.
              </Typography>
            </Box>
          )}

          {/* VRG-471: an ending that does not exist can never receive mail, so the
              Add affordance is replaced by the reason. Before this, a `.cin`
              address rendered the normal card, "Add" worked, and the gift was
              created against an address nobody could ever receive at. */}
          {query && recipientCheck.looksLikeEmail && !recipientCheck.isDeliverable && (
            <Box
              sx={theme => ({
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: '16px',
                p: 2,
              })}
            >
              <Typography
                variant="body2"
                sx={theme => ({ color: theme.palette.error.main, lineHeight: 1.5 })}
              >
                {recipientCheck.reason}
              </Typography>
            </Box>
          )}

          {/* Suggest, never block: this sits ALONGSIDE the Add card for a
              deliverable-but-suspicious address, so an unusual-but-real domain
              stays one tap away. */}
          {query && recipientCheck.suggestion &&
            recipientCheck.suggestion.full !== query.trim() && (
            <Box
              component="button"
              type="button"
              onClick={() => handleInviteUser(recipientCheck.suggestion!.full)}
              sx={theme => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '16px',
                p: 2,
                width: '100%',
                textAlign: 'left',
                background: 'none',
                cursor: 'pointer',
                '&:hover': { backgroundColor: theme.palette.grey[50] },
              })}
            >
              <Typography
                variant="body2"
                sx={theme => ({ color: theme.palette.text.secondary, lineHeight: 1.5 })}
              >
                Did you mean{' '}
                <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  {recipientCheck.suggestion.full}
                </Box>
                ?
              </Typography>
            </Box>
          )}

          {/* Not `length === 0 &&` — see the note above. An email ALWAYS lands here. */}
          {query && recipientCheck.isDeliverable && (
            <Box
              sx={theme => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '16px',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              })}
            >
              <Box
                sx={theme => ({
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.grey[200],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                })}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {query.charAt(0).toUpperCase()}
                </Typography>
              </Box>
              
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {query}
                </Typography>
                <Typography variant="caption" sx={theme => ({ color: theme.palette.text.secondary, fontSize: '14px' })}>
                  {query}
                </Typography>
              </Box>
              
              {/* VERA-304 verify-walkthrough feedback: this doesn't send an
                  invite — it adds the typed email as the gift recipient. */}
              <Button
                variant="outlined"
                onClick={() => handleInviteUser()}
                startIcon={<UserPlus size={16} />}
                sx={theme => ({
                  borderColor: theme.palette.grey[300],
                  color: theme.palette.text.primary,
                  px: 2,
                  '&:hover': {
                    borderColor: theme.palette.grey[400],
                    backgroundColor: theme.palette.grey[50],
                  }
                })}
              >
                Add
              </Button>
            </Box>
          )}
          </Box>

          {/* VRG-358: the scanner is a sheet over this sheet, not a replacement for
              it. Closing it returns the user to the picker with their typing intact,
              so a failed scan costs nothing. */}
          <QrScanner
            open={scanOpen}
            onClose={() => setScanOpen(false)}
            onDecoded={resolveScannedCode}
          />
        </Stack>
      }
      handleOpen={open}
      handleClose={onClose}
    />
  )
}
